import { FC, useEffect } from 'react';
import { Typography, Button } from 'antd';
import { useTranslation } from 'react-i18next';

import { RejectedMessage, AgreeWithTerms } from 'componets/ui';

import { getDeposit } from 'features/funding/actions';

import {Paths, RequestStatus } from 'utils/constants';
import Utils from 'utils/utils';

import { useAppSelector, useAppDispatch, useCopied } from 'hooks';

import { selectUser } from 'selectors';

import { ReactComponent as CopyIcon } from 'assets/icons/copy.svg';

import s from '../index.module.scss';
import {Link} from "react-router-dom";

const BANK_DETAILS_KEYS: { key: string; labelKey: string }[] = [
  { key: 'payment_details', labelKey: 'paymentDetailsText' },
  { key: 'bank', labelKey: 'bankText' },
  { key: 'beneficiaryBankAccountNumber', labelKey: 'bankAccountNumberText' },
  { key: 'beneficiaryBankAddress', labelKey: 'bankAddressText' },
  { key: 'beneficiaryBankSwiftBicCode', labelKey: 'bankSwiftCodeText' },
  { key: 'beneficiaryName', labelKey: 'beneficiaryNameText' },
  { key: 'beneficiaryRegistrationNumber', labelKey: 'beneficiaryRegistrationNumberText' },
];

interface IBankDetails {
	id?: number;
	bank?: string;
	beneficiaryName?: string;
	beneficiaryBankAccountNumber?: string;
	beneficiaryRegistrationNumber?: string;
	beneficiaryBankSwiftBicCode?: string;
	beneficiaryBankAddress?: string;
	payment_details?: string;
}

interface IBankBlockProps {
	bank: IBankDetails;
	accountId: number;
}

const BankBlock = ({ bank, accountId }: IBankBlockProps) => {
	const { t } = useTranslation();
	const { setIsCopied } = useCopied();

	const paymentDetailsVal = t('depositAccountNumber', { id: accountId });

	return (
		<>
			<Typography.Title level={5} className={s.tableTitle}>
				{bank.bank
					? t('bankTransferDetailsToBankText', { bank: bank.bank })
					: t('bankTransferDetailsText')}
			</Typography.Title>
			<div>
				{BANK_DETAILS_KEYS.map(({ key, labelKey }) => {
					const val = key === 'payment_details' ? paymentDetailsVal : (bank as any)[key];

					if (!val) return null;
					return (
						<div key={key} className={s.bankItem}>
							<Typography.Text type="secondary" className={s.bankItemTitle}>
								{t(labelKey)}
							</Typography.Text>
							<div className={s.bankInfo}>
								<Typography.Text>{val}</Typography.Text>
								<Button
									className={s.copy}
									onClick={() => {
										setIsCopied(true);
										Utils.copyText(val);
									}}
									type="link"
									icon={<CopyIcon className="copyIcon" />}
								/>
							</div>
						</div>
					);
				})}
			</div>
		</>
	);
};

export const Deposit: FC = () => {
	const { status, deposit } = useAppSelector((state) => state.funding);

	const user = useAppSelector(selectUser);

	const dispatch = useAppDispatch();

	const { t } = useTranslation();

	useEffect(() => {
		dispatch(getDeposit());
	}, [dispatch]);

	const isQuestionnaire = user.quizStatuses?.KYC && user.quizStatuses?.AML;

	if (status === RequestStatus.REJECTED) {
		return <RejectedMessage />;
	}

	const banks: IBankDetails[] = Array.isArray(deposit) ? deposit : [deposit].filter(Boolean);

	return (
		<>
			<div className={s.paper}>
				{isQuestionnaire ? (
					<>
						<Typography.Title level={4} className={s.title}>
							{t('addFundsAccountText')}
						</Typography.Title>
						<Typography.Text>
						{t(banks.length > 1 ? 'addFundsAccountInfoMultipleBanksText' : 'addFundsAccountInfoText')}
					</Typography.Text>
						{banks[0] && (
							<BankBlock
								bank={banks[0]}
								accountId={user.accountId}
							/>
						)}
					</>
				) : (
					<Typography.Text>
						{t('depositNoQuizText')}&nbsp;<Link to={Paths.KYC}>{t('depositNoQuizText2')}</Link>.
					</Typography.Text>
				)}
			</div>
			{isQuestionnaire && banks.slice(1).map((bank, index) => (
				<div key={bank.id ?? index + 1} className={s.paper}>
					<BankBlock
						bank={bank}
						accountId={user.accountId}
					/>
				</div>
			))}
			{user?.accountId && !user.agreeWithTerms && <AgreeWithTerms />}
		</>
	);
};